import { getCountFromServer } from 'firebase/firestore'
import type { AutoClickerError, AutoClickerErrorType, AutoClickerName } from '../types/AutoClickerErrors'

export interface AutoClickerErrorFilters {
  autoClickerNames?: AutoClickerName[]
  errorTypes?: AutoClickerErrorType[]
  isResolved?: boolean
  plannedDeliveryId?: string
}

function buildQuery(
  filters?: AutoClickerErrorFilters,
  limit?: number,
  cursor?: Cursor,
) {
  const { query } = useFirestore()

  return query<AutoClickerError>('AutoClickerErrors',
    (q) => {
      if (!filters) return

      if (filters.autoClickerNames)
        q.autoClickerName.isAny(...filters.autoClickerNames)

      if (filters.errorTypes)
        q.errorType.isAny(...filters.errorTypes)

      if (filters.plannedDeliveryId)
        q.plannedDeliveryId.is(filters.plannedDeliveryId)

      if (filters.isResolved !== undefined) {
        if (filters.isResolved)
          q.resolvedAt.isNot(null)
        else
          q.resolvedAt.is(null)
      }
    },
    {
      cursor,
      limit,
      sort: [
        {
          name: 'ResolvedAt',
          direction: 'asc',
        },
        {
          name: 'PlannedDeliveryId',
          direction: 'asc',
        },
      ],
    })
}

export default {
  async getAutoClickerCount(
    filters?: AutoClickerErrorFilters,
  ) {
    const source = buildQuery(filters)

    const count = await getCountFromServer(source)

    return count.data().count
  },

  async getAutoClickerErrors<T = AutoClickerError>(options: {
    target: Ref<T[]>
    limit?: number
    filters?: AutoClickerErrorFilters
    signal?: AbortSignal
    cursor?: Cursor
    map?: (error: AutoClickerError) => T
  }) {
    const { target, limit, signal, filters, map, cursor } = options
    const { getDocuments } = useFirestore()

    signal?.throwIfAborted()

    const source = buildQuery(filters, limit, cursor)

    await getDocuments(source, {
      target,
      signal,
      map,
    })
  },
}
